import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Task Flow Error Boundary caught an error:', error, errorInfo);
    
    // In a real app, you'd want to log this to an error reporting service
    // like Sentry, LogRocket, or Bugsnag
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-red-900 via-purple-900 to-slate-900 text-white flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-6">💥</div>
            <h1 className="text-3xl font-bold mb-4 text-red-400">
              Quantum Catastrophe!
            </h1>
            <p className="text-gray-300 mb-6">
              The universe has encountered an unexpected error. Our quantum physicists are working on it.
            </p>
            
            <div className="bg-red-500/20 rounded-lg p-4 mb-6 text-left">
              <div className="text-sm text-red-200 mb-2">Error Details:</div>
              <div className="text-xs text-red-100 font-mono bg-red-900/50 p-2 rounded">
                {this.state.error?.message || 'Unknown error occurred'}
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors duration-300"
              >
                🔄 Reload Universe
              </button>
              
              <button
                onClick={() => this.setState({ hasError: false, error: undefined })}
                className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors duration-300"
              >
                🚀 Try Again
              </button>
            </div>

            <div className="mt-6 text-xs text-gray-500">
              If this keeps happening, please check your quantum connection and try again.
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
